<script setup lang="ts">

import {computed, type Ref, ref, useTemplateRef} from "vue";
import {label} from "../../mixins/translations";
import {useSearchStore} from "../../stores/search";
import {onClickOutside} from '@vueuse/core'
import {Money} from "../../../utils/money";
import {first} from "lodash";

interface Props {
    types?: string[],
}

const props = withDefaults<Props>(defineProps<Props>(), {
    types: []
});

const searchStore = useSearchStore();

const selectedOption : Ref<string> = ref('all');
const searchQuery : Ref<string> = ref('');
const openResults : Ref<boolean> = ref(false);

const searchUrl = computed((): string => {
    return '/search/' + searchQuery.value.toLowerCase() + '/';
})

const navigate = () => {
    if(searchQuery.value.length) {
        window.location.href = searchUrl.value;
    }
}

const preview = async () => {
    await searchStore.getSearchResults(searchQuery.value, selectedOption.value);
    openResults.value = true;
}

const target = useTemplateRef<HTMLElement>('searchbar');
onClickOutside(target, event => openResults.value = false)
</script>

<template>
    <div class="w-full hidden relative lg:block">
        <div ref="searchbar" @click="openResults = true;" class="w-full relative h-12 bg-white hidden xl:flex items-center border border-gray-200 rounded-lg focus-within:shadow-lg">
            <input class="outline-none focus:outline-none focus:ring-0 bg-transparent border-0 font-normal text-sm pl-4 w-full" type="text" v-model="searchQuery" :placeholder="label('site.search.placeholder')" @keyup.enter="navigate" @keyup="preview">
            <svg xmlns="http://www.w3.org/2000/svg" class="ml-auto h-5 px-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
        </div>
        <div class="w-full absolute left-0 top-14 rounded-xl z-50" v-show="openResults" v-if="searchStore.results">
            <div class="flex flex-col md:flex-row min-w-[800px] rounded-xl shadow shadow-lg">
                <div class="w-full md:w-2/3 bg-white rounded-l-xl">
                    <div class="flex items-center justify-between px-3 py-4">
                        <h2 class="text-xl font-bold flex items-center">
                            {{ label('site.search.products') }}
                            <span class="bg-[#5CA5BE] text-white text-xs px-3 ml-2 py-1 rounded-full">{{ searchStore.results.products?.count }}</span>
                        </h2>
                        <a :href="searchUrl" class="text-sm bg-gray-100 px-3 py-1 rounded hover:bg-blue-50 cursor-pointer">{{ label('site.search.show-all-button') }}</a>
                    </div>
                    <div class="max-h-[400px] overflow-y-auto rounded-bl-xl">
                        <div v-if="searchStore.results.products?.count > 0">
                            <div class="flex items-start border-t border-gray-200 hover:bg-gray-100 p-4" v-for="product in searchStore.results.products?.items">
                                <a :href="`/${product.slug}/`">
                                    <img :src="product.thumbnail.image_url" :alt="product.name" class="w-16 object-fit mr-4">
                                </a>
                                <div class="flex-1 flex">
                                    <a :href="`/${product.slug}/`" class="flex-1 flex flex-col gap-3">
                                        <h3 class="font-normal">{{ product.name }}</h3>
                                        <p class="text-lg font-semibold" v-if="product.price?.price_incl">
                                            {{ Money.format(product.price.price_incl, product.price.currency) }}
                                            <span class="text-sm text-gray-500 font-normal">{{ label('site.order.incl_vat') }}</span>
                                        </p>
                                    </a>
                                </div>
                                <div class="flex items-center space-x-2">
                                    <span class="w-3 h-3 bg-green-500 rounded-full"></span>
                                    <button class="p-2 hover:bg-gray-100 rounded-sm">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5">
                                            <circle cx="8" cy="21" r="1"></circle>
                                            <circle cx="19" cy="21" r="1"></circle>
                                            <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div v-else class="p-4 border-t border-gray-200">
                            {{ label('site.search.no-results') }}
                        </div>
                    </div>
                </div>
                <div class="w-full md:w-1/3 bg-[#F6FCFF] p-6 space-y-6 rounded-r-xl">
                    <div>
                        <h2 class="font-bold text-base mb-2">
                            {{ label('site.search.pages') }}
                            <span class="bg-[#5CA5BE] text-white text-xs px-3 py-1 rounded-full">{{ searchStore.results.pages?.count }}</span>
                        </h2>
                        <ul class="space-y-1 text-base" v-if="searchStore.results.pages?.count > 0">
                            <li v-for="page in searchStore.results.pages?.items">{{ page.title }}</li>
                        </ul>
                    </div>
                    <div>
                        <h2 class="font-bold text-base mb-2">
                            {{ label('site.search.projects') }}
                            <span class="bg-[#5CA5BE] text-white text-xs px-3 py-1 rounded-full">{{ searchStore.results.projects?.count }}</span>
                        </h2>
                        <div class="flex space-x-3" v-if="searchStore.results.projects?.count > 0">
                            <a :href="`${first(searchStore.results.projects.items).full_slug}`">
                                <img :src="first(searchStore.results.projects.items).image_path" :alt="first(searchStore.results.projects.items).title" class="w-12 h-12 object-cover rounded-md">
                                <p class="font-semibold text-sm leading-tight">{{ first(searchStore.results.projects.items).title }}</p>
                            </a>
                        </div>
                    </div>
                    <div>
                        <h2 class="font-bold text-base mb-2">
                            {{ label('site.search.articles') }}
                            <span class="bg-[#5CA5BE] text-white text-xs px-3 py-1 rounded-full">{{ searchStore.results.articles?.count }}</span>
                        </h2>
                        <div class="flex space-x-3" v-if="first(searchStore.results.articles.items)">
                            <a :href="`${first(searchStore.results.articles.items).full_slug}`">
                                <img :src="first(searchStore.results.articles.items).image_path" :alt="first(searchStore.results.articles.items).title" class="w-12 h-12 object-cover rounded-md">
                                <p class="font-semibold text-sm leading-tight">{{ first(searchStore.results.articles.items).title }}</p>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
